/**
 * Chip emoji contract (universal, adapted from the APL documentation emoji
 * contract). Every status chip is emoji + label; the emoji replaces the chip
 * dot and repeats in filters and the legend page.
 */

const DOC_STATUS = {
  draft: { emoji: '📝', label: 'Черновик' },
  pending: { emoji: '⏳', label: 'В ожидании' },
  processing: { emoji: '🔄', label: 'Обработка' },
  indexed: { emoji: '✅', label: 'Индексирован' },
  error: { emoji: '❌', label: 'Ошибка' },
  archived: { emoji: '🗄️', label: 'Архив' },
};

const DOC_TYPE = {
  lecture: { emoji: '🎦', label: 'Лекция' },
  methodical: { emoji: '📖', label: 'Методичка' },
  faq: { emoji: '❓', label: 'FAQ' },
  instruction: { emoji: '🧾', label: 'Инструкция' },
  glossary: { emoji: '🔤', label: 'Глоссарий' },
  example: { emoji: '💡', label: 'Пример' },
  external: { emoji: '🔗', label: 'Внешний ресурс' },
};

const PUBLISHED = { emoji: '📤', label: 'Опубликован' };

const MODALITY = {
  rag: { emoji: '🔍', label: 'RAG' },
  text: { emoji: '📄', label: 'TEXT' },
  lms: { emoji: '🎓', label: 'LMS' },
  mixed: { emoji: '🔀', label: 'MIXED' },
  log: { emoji: '📜', label: 'LOG' },
};

const HEALTH = {
  ok: { emoji: '🟢', label: 'Норма' },
  error: { emoji: '❌', label: 'Ошибка' },
  warning: { emoji: '⚠️', label: 'Внимание' },
  degraded: { emoji: '🟠', label: 'Деградация' },
  disabled: { emoji: '🚫', label: 'Отключено' },
  unknown: { emoji: '➖', label: 'Н/д' },
};

const LIFECYCLE_STATUS = {
  // Text-presentation check mark (U+2714 + FE0E): rendered as a font glyph so
  // CSS can color it green. Distinct from DOC_STATUS.indexed, whose ✅ is the
  // colored "white check on a green square". Green coloring comes from the
  // .ai-status--check class (see index.css).
  success: { emoji: '✔︎', label: 'Успех' },
  error: { emoji: '❌', label: 'Ошибка' },
  pending: { emoji: '🔄', label: 'В процессе' },
};

const SESSION_ACTIVE = {
  active: { emoji: '⚡', label: 'Активна' },
  inactive: { emoji: '⏸️', label: 'Неактивна' },
};

const RETRIEVAL = {
  hit: { emoji: '🎯', label: 'hit' },
  miss: { emoji: '💨', label: 'miss' },
};

const CACHE = { emoji: '💾', label: 'кэш' };

const FALLBACK = { emoji: '🛟', label: 'fallback' };

// Project-specific dialog intents (characteristic of AI Curator, not
// universal). Glyphs are unique against every other classification; error
// shares ❌ with the generic "Ошибка" concept (one emoji — one concept).
const INTENT = {
  study: { emoji: '📚', label: 'Учебный' },
  organizational: { emoji: '🗓️', label: 'Организационный' },
  mixed: { emoji: '🧩', label: 'Смешанный' },
  progress: { emoji: '📈', label: 'Прогресс' },
  deadline: { emoji: '⏰', label: 'Дедлайн' },
  out_of_scope: { emoji: '🚧', label: 'Не распределено' },
  error: { emoji: '❌', label: 'Ошибка' },
};

/** Modifies .ai-status to hide the dot: emoji replaces it. */
export const CHIP_NO_DOT = 'ai-status--emoji';

/**
 * Audit roles — shared emoji alphabet with the RF reference console
 * (review-flow): one emoji — one concept across the portfolio. Labels are
 * product-local (RF calls the same 👤 role «Клиент», here it is «Студент»).
 * `system` covers backend service events (no RF counterpart); a null role
 * renders no chip.
 */
export const AUDIT_ROLE = {
  admin: { emoji: '🛡️', label: 'Администратор' },
  student: { emoji: '👤', label: 'Студент' },
  operator: { emoji: '🎧', label: 'Оператор' },
  demo: { emoji: '🎭', label: 'Демо' },
  system: { emoji: '⚙️', label: 'Система' },
};

export function chipText(chip) {
  if (!chip) return '—';
  return `${chip.emoji} ${chip.label}`;
}

/** Двойная подсказка «Параметр: Значение» (native title; владелец: во всех
 *  консолях) — например «Тональность: Агрессивный». */
export function chipTitleText(family, chip) {
  if (!chip) return undefined;
  return family ? `${family}: ${chip.label}` : chip.label;
}

export function chipEntry(map, key) {
  return map[key] || null;
}

export { DOC_STATUS, DOC_TYPE, PUBLISHED, MODALITY, HEALTH, LIFECYCLE_STATUS, SESSION_ACTIVE, RETRIEVAL, CACHE, INTENT, FALLBACK };